import React from 'react';
import { ArrowRight, LayoutGrid, Shield, Clock, Home, Settings, Monitor, Smartphone, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from './ui/Link';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from './ui/Button';
import { APP_CONFIG } from '../config/app';

interface ActivityCardProps {
  icon: React.ReactNode;
  image: string;
  title: string;
  description: string;
  ctaHref: string;
  delay?: number;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ icon, image, title, description, ctaHref, delay = 0 }) => {
  return (
    <motion.div
      className="group relative backdrop-blur-sm rounded-xl transition-all duration-300 overflow-hidden border flex flex-col h-full bg-white/5 hover:bg-white/10 border-white/5 hover:border-white/10"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay, ease: 'easeOut' }}
      whileHover={{ y: -6, scale: 1.01 }}
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="relative z-10 flex flex-col flex-1 card-spacing content-spacing">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#5865F2]/10 flex items-center justify-center text-[#5865F2] shrink-0">
            {icon}
          </div>
          <h3 className="text-white text-lg font-semibold leading-tight">{title}</h3>
        </div>
        <p className="text-gray-400 text-sm">{description}</p>

        <div className="mt-auto pt-4">
          <Link
            href={ctaHref}
            className="w-full group-hover:bg-gradient-to-r group-hover:from-[#5865F2] group-hover:via-[#4752C4] group-hover:to-[#5865F2] group-hover:text-white text-[#5865F2] bg-gradient-to-r from-[#5865F2]/10 via-[#4752C4]/10 to-[#5865F2]/10 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 flex items-center justify-center"
          >
            <span>Découvrir</span>
            <ArrowRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const ActivitiesOverview: React.FC = () => {
  const activities = [
    {
      icon: <Monitor size={16} />,
      image: '/images/depannage-informatique.png',
      title: 'Dépannage informatique',
      description: 'PC, Mac, réseau, sécurité — à distance ou à domicile.',
      ctaHref: '/depannage-informatique',
    },
    {
      icon: <Smartphone size={16} />,
      image: '/images/reparation-telephone.png',
      title: 'Réparation téléphone',
      description: 'Écran cassé, batterie, connecteur de charge — devis immédiat.',
      ctaHref: '/reparation-telephone',
    },
    {
      icon: <Globe size={16} />,
      image: '/images/site-web.png',
      title: 'Création de site web',
      description: 'Site vitrine ou e-commerce, design moderne, SEO inclus.',
      ctaHref: '/creation-site-web',
    },
  ];

  return (
    <section id="activities" className="section-spacing bg-gradient-to-b from-[#2F3136] to-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center section-title-spacing">
          <div className="section-subtitle-badge">
            <LayoutGrid size={14} className="mr-1.5" />
            <span className="text-xs font-medium">Nos 3 activités</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Trois expertises, <span className="text-[#5865F2]">un seul interlocuteur</span>
          </h2>
          <p className="text-gray-300 max-w-xl mx-auto text-sm">
            Informatique, téléphonie et web : choisissez l'activité qui correspond à votre besoin.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {activities.map((activity, index) => (
            <ActivityCard
              key={activity.title}
              icon={activity.icon}
              image={activity.image}
              title={activity.title}
              description={activity.description}
              ctaHref={activity.ctaHref}
              delay={index * 0.08}
            />
          ))}
        </div>

        <motion.div
          className="mt-16 text-center bg-[#2F3136] p-8 rounded-xl border border-gray-700 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="flex items-center justify-center mb-4">
            <Shield className="text-[#5865F2] h-8 w-8 mr-3" />
            <h3 className="text-2xl font-bold text-white">Besoin d'un service <span className="text-[#5865F2]">personnalisé ?</span></h3>
          </div>
          <p className="text-gray-300 mb-4 max-w-2xl mx-auto">
            Nous proposons des solutions sur mesure adaptées à vos besoins spécifiques. Contactez-nous pour un devis gratuit.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-6">
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Clock className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Intervention +60min</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Home className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Domicile non couvert par un expert</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Settings className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Configuration pour les TPE/PME</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Monitor className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Matériel spécifique</span>
            </div>
          </div>
          <RouterLink to={APP_CONFIG.routes.contact}>
            <Button variant="primary" size="lg" className="px-8">
              Demander un devis personnalisé
            </Button>
          </RouterLink>
        </motion.div>
      </div>
    </section>
  );
};

export default ActivitiesOverview;
